import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { ArrowPathIcon } from "@heroicons/react/24/solid";

export type ImportProgress = {
	message: string;
	phase: string;
	percent: number;
	pause?: boolean;
};

interface ImportProgressBarProps {
	progress?: ImportProgress | null;
}

const clampPercent = (n: number) => Math.min(100, Math.max(0, n));

export default function ImportProgressBar({ progress }: ImportProgressBarProps) {
	const rawPercent = progress?.percent;
	const percent = typeof rawPercent === "number" ? clampPercent(rawPercent) : undefined;

	const phase = progress?.phase?.trim();
	const message = progress?.message ?? "Processing...";

	const isIndeterminate = percent === undefined;
	const isComplete = percent === 100;
	const showSpinner = !isIndeterminate && !isComplete;
	const showPaused = !isIndeterminate && !isComplete && progress?.pause;

	const barColorClass = !isIndeterminate && !isComplete ? "bg-primary-500" : "bg-green-500";

	const label = phase ? `${phase}: ${message}` : message;
	const ariaValueText = isIndeterminate ? message : `${percent}% - ${message}`;

	return (
		<div className="w-full">
			<div className="flex items-center justify-between text-xs text-gray-600">
				<div className="truncate flex gap-2 items-center">
					{showPaused && (
						<ExclamationTriangleIcon
							className="size-5 text-yellow-500"
							aria-hidden="true"
						/>
					)}
					{showSpinner && !showPaused && (
						<ArrowPathIcon className="size-3.5 animate-spin" aria-hidden="true" />
					)}
					{phase && <span className="font-medium text-gray-700">{phase}:</span>}
					<span>{message}</span>
				</div>

				{!isIndeterminate && <div className="ml-3 text-gray-700">{percent}%</div>}
			</div>

			<div
				className="mt-2 h-2 w-full overflow-hidden rounded-full border border-gray-200 bg-white"
				role="progressbar"
				aria-label={phase ?? "Import progress"}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={isIndeterminate ? undefined : percent}
				aria-valuetext={ariaValueText}
				title={label}>
				{isIndeterminate ? (
					<div className="h-full w-1/3 animate-pulse rounded-full bg-gray-200" />
				) : (
					<div
						className={`h-full rounded-full transition-[width] duration-300 ${barColorClass} ${
							!isComplete && !showPaused
								? "bg-[repeating-linear-gradient(135deg,rgba(255,255,255,0.25)_0px,rgba(255,255,255,0.25)_10px,transparent_10px,transparent_20px)] animate-pulse"
								: !isComplete && showPaused
									? "bg-[repeating-linear-gradient(135deg,rgba(0,0,0,0.06)_0px,rgba(0,0,0,0.06)_10px,transparent_10px,transparent_20px)]"
									: ""
						}`}
						style={{ width: `${percent}%` }}
					/>
				)}
			</div>
		</div>
	);
}
